import { useCallback, useEffect, useState } from "react";

export const TRANSITION_STORAGE_KEY = "userSeenRecommendationsTransition";
export const TRANSITION_COMPLETE_ANIMATION = "hideOverlay";
const RECENCY_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Hook to track whether the user has seen the transition.
 *
 * Tracks whether the user has seen the transition within the last 60 minutes.
 * The timestamp is only stored when `setHasSeen` is called (typically on animation end),
 * so users who navigate away before completion will see the full animation again.
 *
 * @returns Object containing:
 *   - `isReady`: Whether client-side hydration is complete
 *   - `hasSeen`: Whether user has seen the transition within the recency window
 *   - `setHasSeen`: Function to call when transition completes to store the timestamp
 *
 * @example
 * ```tsx
 * const { isReady, hasSeen, setHasSeen } = useHasSeenTransition();
 *
 * const handleAnimationEnd = () => {
 *   setHasSeen();
 *   onComplete?.();
 * };
 *
 * if (!isReady) return null;
 * return hasSeen ? <FallbackSpinner /> : <FullAnimation />;
 * ```
 */
export const useHasSeenTransition = () => {
  const [isReady, setIsReady] = useState(false);
  const [hasSeen, setHasSeenState] = useState(false);

  useEffect(() => {
    const storage = window.localStorage;
    if (!storage) {
      setIsReady(true);
      return;
    }

    const seenAt = parseInt(storage.getItem(TRANSITION_STORAGE_KEY) ?? "", 10);
    const isRecent = !Number.isNaN(seenAt) && Date.now() - seenAt < RECENCY_MS;

    if (isRecent) {
      setHasSeenState(true);
    }

    setIsReady(true);
  }, []);

  const setHasSeen = useCallback(() => {
    try {
      window.localStorage?.setItem(TRANSITION_STORAGE_KEY, String(Date.now()));
    } catch {
      // Ignore storage errors
    }
  }, []);

  return { isReady, hasSeen, setHasSeen };
};
